import { windowsTableStyles } from "../styles/windowsTableStyles";

import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { createSalesDocument, getSalesDocument, updateSalesDocument, getSalesDocuments } from "../redux/actions/salesDocumentActions";
import { fetchFiles, getFile } from "../redux/actions/fileActions";
import { fetchRelationships } from "../redux/actions/relationshipActions";
import { listDisbursements, updateIsBilledDisbursements } from "../redux/actions/disbursementActions";
import { updateIsBilled } from "../redux/actions/timesheetActions";
import FileAutocomplete from "../components/FileAutocomplete";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import axios from "../utils/axiosConfig";

import { useParams } from "react-router-dom";
import Calendar from "@mui/icons-material/Event";
import { SingleInputDateRangeField } from "@mui/x-date-pickers-pro/SingleInputDateRangeField";
import enGB from "date-fns/esm/locale/en-GB";
import { DatePicker } from "@mui/x-date-pickers";

import format from "date-fns/esm/format";
import parseISO from "date-fns/esm/parseISO";
import parse from "date-fns/esm/parse";


import {
  Autocomplete,
  TextField,
  Button,
  MenuItem,
  Grid,
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Card,
  CardContent,
  Container,
  Stack,
  Breadcrumbs,
  Link,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  CircularProgress,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  Menu,
  IconButton,
  Select,
  FormControl,
  InputLabel,
  Avatar,
  InputAdornment
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useNavigate, useLocation } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";

import CustomAutocomplete from "../components/CustomAutocomplete";
import { textFieldStyles } from "../styles/textFieldStyles";
import BreadcrumbNav from "../components/BreadcrumbNav";
import CancelButton from "../components/CancelButton";
import SubmitButton from "../components/SubmitButton";

const getCurrentDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDueDateDefault = () => {
  const today = new Date();
  const dueDate = new Date(today);
  dueDate.setDate(today.getDate() + 14); // Add 14 days
  const year = dueDate.getFullYear();
  const month = String(dueDate.getMonth() + 1).padStart(2, "0");
  const day = String(dueDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};


const MUIDatePicker = ({ value, onChange, sx, label }) => {
  // const parsed = value ? parseISO(value) : null;
  const parsed = value ? parse(value, 'yyyy-MM-dd', new Date()) : null;

  console.log("date is", parsed)


  return (
    <FormControl variant="outlined" size="small" sx={sx}>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={enGB}
      >
        <DatePicker
          label={label}
          value={parsed}
          onChange={(newDate) => {
            if (newDate) {
              onChange(format(newDate, 'yyyy-MM-dd'));
            } else {
              onChange('');
            }
          }}
          inputFormat="dd/MM/yyyy"
          mask="__/__/____"
          slotProps={{
            textField: {
              size: 'small',
              sx: {
                // target the OutlinedInput root:
                '& .MuiOutlinedInput-root': {
                  height: 33.53,            // your desired total height
                  paddingRight: 2,       // remove extra right padding if any
                },
                // tweak the inner input padding if needed:
                '& .MuiOutlinedInput-input': {
                  padding: '8px 14px',
                },
                // shrink the calendar icon if it's too big:
                '& .MuiInputAdornment-root svg': {
                  fontSize: '1.2rem',
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    </FormControl>
  );
};

// Helper function to extract code from description (e.g., "COCL - Correspondentie..." -> "COCL")
const extractCodeFromDescription = (description) => {
  if (!description) return '';
  // Check if description contains a code pattern (e.g., "COCL - " or "COCL:")
  // Pattern matches uppercase codes (2-10 chars) followed by space and dash/colon
  const codeMatch = description.match(/^([A-Z]{2,10})(\s*[-:]?\s*)/);
  if (codeMatch) {
    return codeMatch[1];
  }
  // If no code pattern found, return empty string (will fall back to type)
  return '';
};

const CreateInvoicePage = ({ isEditing = false, initialData = null, onSubmit = null }) => {
  const { t, i18n } = useTranslation(['pages', 'forms', 'buttons', 'common', 'messages']);

  // Item description constants for logic comparisons (always in English for consistency)
  const ITEM_KEYS = {
    FEES: 'Fees',
    OFFICE_EXPENSES_ON_FEES: 'Office Expenses on Fees',
    EXPENSES: 'Expenses',
    HIGH_DISBURSEMENT: 'High Disbursement',
    VAT: 'VAT',
    CREDITS_ON_FEES: 'Credits on Fees',
    CREDITS_ON_EXPENSES: 'Credits on Expenses',
    CREDITS_ON_PROVISIONS_ON_EXPENSES: 'Credits on Provisions on Expenses',
    CREDITS_ON_DISBURSEMENTS_HIGH_VAT: 'Credits on Disbursements High VAT',
    CREDITS_ON_PROVISIONS_ON_FEES: 'Credits on Provisions on Fees',
    CREDITS_ON_PROVISIONS_ON_DISBURSEMENTS_HIGH_VAT: 'Credits on Provisions on Disbursements High VAT',
    AMOUNT_SUBJECT_TO_HIGH_VAT: 'Amount subject to High VAT',
    CREDITS_ON_DISBURSEMENTS_LOW_VAT: 'Credits on Disbursements Low VAT',
    CREDITS_ON_PROVISIONS_ON_DISBURSEMENTS_LOW_VAT: 'Credits on Provisions on Disbursements Low VAT',
    AMOUNT_SUBJECT_TO_LOW_VAT: 'Amount subject to Low VAT',
    CREDITS_ON_DISBURSEMENTS_NO_VAT: 'Credits on Disbursements no VAT',
    CREDITS_ON_PROVISIONS_ON_DISBURSEMENTS_NO_VAT: 'Credits on Provisions on Disbursements no VAT',
    PROVISION_ON_FEES: 'Provision on Fees',
    PROVISIONS_ON_EXPENSES: 'Provisions on Expenses',
    PROVISIONS_ON_DISBURSEMENTS_HIGH_VAT: 'Provisions on Disbursements High VAT',
    PROVISIONS_ON_DISBURSEMENTS_LOW_VAT: 'Provisions on Disbursements Low VAT',
    PROVISIONS_ON_DISBURSEMENTS_NO_VAT: 'Provisions on Disbursements no VAT',
    AMOUNT_NOT_SUBJECT_TO_VAT: 'Amount not subject to VAT'
  };

  // Expense type constants for consistency
  const EXPENSE_TYPES = {
    KILOMETER: t('common:kilometer'),
    COST: t('common:cost')
  };

  // Helper function to get translated item descriptions for display
  const getTranslatedDescription = (englishKey) => {
    const translationMap = {
      [ITEM_KEYS.FEES]: t('forms:billing.items.fees'),
      [ITEM_KEYS.OFFICE_EXPENSES_ON_FEES]: t('forms:billing.items.office_expenses_on_fees'),
      [ITEM_KEYS.EXPENSES]: t('forms:billing.items.expenses'),
      [ITEM_KEYS.HIGH_DISBURSEMENT]: t('forms:billing.items.high_disbursement'),
      [ITEM_KEYS.VAT]: t('forms:billing.items.vat'),
      [ITEM_KEYS.CREDITS_ON_FEES]: t('forms:billing.items.credits_on_fees'),
      [ITEM_KEYS.CREDITS_ON_EXPENSES]: t('forms:billing.items.credits_on_expenses'),
      [ITEM_KEYS.CREDITS_ON_PROVISIONS_ON_EXPENSES]: t('forms:billing.items.credits_on_provisions_on_expenses'),
      [ITEM_KEYS.CREDITS_ON_DISBURSEMENTS_HIGH_VAT]: t('forms:billing.items.credits_on_disbursements_high_vat'),
      [ITEM_KEYS.CREDITS_ON_PROVISIONS_ON_FEES]: t('forms:billing.items.credits_on_provisions_on_fees'),
      [ITEM_KEYS.CREDITS_ON_PROVISIONS_ON_DISBURSEMENTS_HIGH_VAT]: t('forms:billing.items.credits_on_provisions_on_disbursements_high_vat'),
      [ITEM_KEYS.AMOUNT_SUBJECT_TO_HIGH_VAT]: t('forms:billing.items.amount_subject_to_high_vat'),
      [ITEM_KEYS.CREDITS_ON_DISBURSEMENTS_LOW_VAT]: t('forms:billing.items.credits_on_disbursements_low_vat'),
      [ITEM_KEYS.CREDITS_ON_PROVISIONS_ON_DISBURSEMENTS_LOW_VAT]: t('forms:billing.items.credits_on_provisions_on_disbursements_low_vat'),
      [ITEM_KEYS.AMOUNT_SUBJECT_TO_LOW_VAT]: t('forms:billing.items.amount_subject_to_low_vat'),
      [ITEM_KEYS.CREDITS_ON_DISBURSEMENTS_NO_VAT]: t('forms:billing.items.credits_on_disbursements_no_vat'),
      [ITEM_KEYS.CREDITS_ON_PROVISIONS_ON_DISBURSEMENTS_NO_VAT]: t('forms:billing.items.credits_on_provisions_on_disbursements_no_vat'),
      [ITEM_KEYS.PROVISION_ON_FEES]: t('forms:billing.items.provision_on_fees'),
      [ITEM_KEYS.PROVISIONS_ON_EXPENSES]: t('forms:billing.items.provisions_on_expenses'),
      [ITEM_KEYS.PROVISIONS_ON_DISBURSEMENTS_HIGH_VAT]: t('forms:billing.items.provisions_on_disbursements_high_vat'),
      [ITEM_KEYS.PROVISIONS_ON_DISBURSEMENTS_LOW_VAT]: t('forms:billing.items.provisions_on_disbursements_low_vat'),
      [ITEM_KEYS.PROVISIONS_ON_DISBURSEMENTS_NO_VAT]: t('forms:billing.items.provisions_on_disbursements_no_vat'),
      [ITEM_KEYS.AMOUNT_NOT_SUBJECT_TO_VAT]: t('forms:billing.items.amount_not_subject_to_vat')
    };
    return translationMap[englishKey] || englishKey;
  };
  const tableStyles = windowsTableStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  // const urlParams = new URLSearchParams(location.search);
  // const id = urlParams.get('id');
  const locationCaseId = location.state?.caseId;
  const storedCaseId = localStorage.getItem("selectedCaseId");
  const initialCase = locationCaseId || storedCaseId || "";



  const initialFrom = location.state?.from?._id || "";
  const initialTo = location.state?.to?._id || "";

  const [formData, setFormData] = useState(() => {
    // Try to restore from localStorage first, but only if no locationCaseId is present
    const savedState = localStorage.getItem('createInvoiceFormState');
    if (savedState && !initialData && !locationCaseId) {
      try {
        const parsedState = JSON.parse(savedState);
        const savedTime = new Date(parsedState.timestamp);
        const now = new Date();
        const hoursDiff = (now - savedTime) / (1000 * 60 * 60);
        
        if (hoursDiff < 24) {
          return parsedState.formData;
        }
      } catch (error) {
        console.error('Error restoring form state:', error);
      }
    }

    // Fall back to original initialization logic
    if (initialData) {
      const processedData = JSON.parse(JSON.stringify(initialData));
      const expensesItem = processedData.items.find(item => item.description === ITEM_KEYS.EXPENSES);
      const disbursementsItem = processedData.items.find(item => item.description === ITEM_KEYS.HIGH_DISBURSEMENT);

      return {
        ...processedData,
        items: processedData.items.map(item => ({
          ...item,
          value: Number(item.value || 0)
        }))
      };
    }
    return {
      case: initialCase || '',
      from: initialFrom || '',
      to: initialTo || '',
      type: t('forms:billing.types.invoice'),
      date: getCurrentDate(),
      dueDate: getDueDateDefault(),
      status: t('forms:billing.status.unpaid'),
      items: [
        { description: ITEM_KEYS.FEES, value: 0.0, timesheets: [] },
        { description: ITEM_KEYS.OFFICE_EXPENSES_ON_FEES, value: 0.0 },
        { description: ITEM_KEYS.EXPENSES, value: 0.0 },
        { description: ITEM_KEYS.HIGH_DISBURSEMENT, value: 0.0, disbursements: [] },
        { description: ITEM_KEYS.VAT, value: 0.0 },
      ],
      totalDue: 0.0,
      total: 0.0,
      VAT: 0.0,
    };
  });


  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        items: initialData.items.map(item => ({
          ...item,
          value: Number(item.value || 0),
        })),
      });

      // Initialize selectedDisbursements from initialData
      const disbursementsItem = initialData.items.find(item => item.description === ITEM_KEYS.HIGH_DISBURSEMENT);
      setSelectedDisbursements(disbursementsItem?.disbursements || []);
    }
  }, [initialData]);



  // Add state variables for VAT and Office Expenses percentages
  const [vatPercentage, setVatPercentage] = useState(21);
  const [officeExpensesPercentage, setOfficeExpensesPercentage] = useState(7);

  const { user } = useSelector((state) => state.userLoaded);
  const { files } = useSelector((state) => state.fileList);
  const { relationships } = useSelector((state) => state.relationshipList);
  const { disbursements } = useSelector((state) => state.disbursementList || { disbursements: [] });
  const { salesDocument, loading: salesDocLoading, error: salesDocError } = useSelector((state) => state.salesDocumentDetails);

  // Local timesheets state for the invoice screens.
  // This decouples invoice timesheet loading from the global Redux store
  // and ensures we always fetch fresh data from the backend when filters change.
  const [timesheets, setTimesheets] = useState([]);
  const [timesheetsLoading, setTimesheetsLoading] = useState(false);

  const [selectedDisbursements, setSelectedDisbursements] = useState([]);
  const [selectedTimesheets, setSelectedTimesheets] = useState([]);
  const [selectedTimesheets2, setSelectedTimesheets2] = useState([]);
  const [filteredRelationships, setFilteredRelationships] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [isInputActive, setIsInputActive] = useState(true);
  const [showAutocomplete, setShowAutocomplete] = useState(false); // State to control visibility
  const [userRole, setUserRole] = useState(null);
  const [fromFieldCleared, setFromFieldCleared] = useState(false);

  const [billingPeriod, setBillingPeriod] = useState([null, null]);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [expandedAccordion, setExpandedAccordion] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [expenseRows, setExpenseRows] = useState(() => {
    if (initialData) {
      const expensesItem = initialData.items.find(item => item.description === ITEM_KEYS.EXPENSES);
      return expensesItem?.expenses?.map(exp => ({
        ...exp,
        date: new Date(exp.date).toISOString().split('T')[0],
        amount: Number(exp.amount || 0),
        rate: Number(exp.rate || 0),
        quantity: Number(exp.quantity || 0),
        isEditable: false
      })) || [];
    }
    return [];
  });

  const [disbursementRows, setDisbursementRows] = useState(() => {
    if (initialData) {
      const disbursementsItem = initialData.items.find(item => item.description === ITEM_KEYS.HIGH_DISBURSEMENT);
      return disbursementsItem?.disbursements?.map(disb => ({
        ...disb,
        date: new Date(disb.date).toISOString().split('T')[0],
        amount: Number(disb.amount || 0),
        isEditable: false
      })) || [];
    }
    return [];
  });

  // Function to save form state to localStorage
  const saveFormState = () => {
    const stateToSave = {
      formData,
      selectedDisbursements,
      selectedTimesheets,
      selectedTimesheets2,
      expenseRows,
      disbursementRows,
      vatPercentage,
      officeExpensesPercentage,
      billingPeriod,
      expandedAccordion,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('createInvoiceFormState', JSON.stringify(stateToSave));
    console.log('💾 Form state saved to localStorage', {
      case: stateToSave.formData.case,
      from: stateToSave.formData.from,
      to: stateToSave.formData.to,
      selectedTimesheets: stateToSave.selectedTimesheets2.length,
      selectedDisbursements: stateToSave.selectedDisbursements.length
    });
  };

  // Function to restore form state from localStorage
  // Optionally restrict restore to a specific caseId to avoid applying
  // stale state from a different matter.
  const restoreFormState = (expectedCaseId = null) => {
    const savedState = localStorage.getItem('createInvoiceFormState');
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        // Check if saved state is not too old (24 hours)
        const savedTime = new Date(parsedState.timestamp);
        const now = new Date();
        const hoursDiff = (now - savedTime) / (1000 * 60 * 60);
        
        if (hoursDiff < 24) {
          // If an expectedCaseId is provided, ensure the saved state's case matches.
          if (
            expectedCaseId &&
            parsedState.formData &&
            parsedState.formData.case &&
            parsedState.formData.case !== expectedCaseId
          ) {
            console.log(
              '⏭️ Skipping restore: saved form case does not match expected caseId',
              { savedCase: parsedState.formData.case, expectedCaseId }
            );
            return false;
          }

          // Recompute Fees from restored selected timesheets to avoid stale values
          try {
            const selectedTs = parsedState.selectedTimesheets2 || [];
            const feesFromSelected = selectedTs.reduce((sum, ts) => sum + Number(ts?.totalAmount || 0), 0);
            if (Array.isArray(parsedState.formData?.items)) {
              parsedState.formData.items = parsedState.formData.items.map((item) => {
                if (item.description === ITEM_KEYS.FEES) {
                  return {
                    ...item,
                    value: Number(feesFromSelected.toFixed(2)),
                    // Keep the FEES item timesheets in sync with restored selection
                    timesheets: selectedTs,
                  };
                }
                return item;
              });
            }
          } catch (e) {
            console.warn('⚠️ Failed to recompute Fees from restored state, defaulting to 0', e);
          }
          setFormData(parsedState.formData);
          // Don't auto-restore selectedDisbursements to prevent unwanted selection
          // User should manually select disbursements each time
          setSelectedDisbursements([]);
          setSelectedTimesheets(parsedState.selectedTimesheets || []);
          setSelectedTimesheets2(parsedState.selectedTimesheets2 || []);
          setExpenseRows(parsedState.expenseRows || []);
          setDisbursementRows(parsedState.disbursementRows || []);
          setVatPercentage(parsedState.vatPercentage || 21);
          setOfficeExpensesPercentage(parsedState.officeExpensesPercentage || 7);
          setBillingPeriod(parsedState.billingPeriod || [null, null]);
          setExpandedAccordion(parsedState.expandedAccordion || null);
          console.log('✅ Form state restored from localStorage', {
            case: parsedState.formData.case,
            from: parsedState.formData.from,
            to: parsedState.formData.to,
            selectedTimesheets: parsedState.selectedTimesheets2?.length || 0,
            selectedDisbursements: parsedState.selectedDisbursements?.length || 0
          });
          return true;
        } else {
          console.log('⏰ Saved form state is too old (>24 hours), clearing...');
          clearSavedFormState();
        }
      } catch (error) {
        console.error('❌ Error restoring form state:', error);
      }
    }
    return false;
  };

  // Function to clear saved form state
  const clearSavedFormState = () => {
    localStorage.removeItem('createInvoiceFormState');
    console.log('🗑️ Saved form state cleared from localStorage');
  };

  // Restore form state from localStorage on component mount
  // Note: selectedDisbursements are NOT restored to prevent unwanted auto-selection
  useEffect(() => {
    // Determine the "active" case context if any:
    // - Prefer explicit caseId passed via navigation state
    // - Otherwise fall back to any initially resolved case (e.g. from localStorage)
    const activeCaseId = locationCaseId || initialCase || '';

    if (!initialData && !isEditing) {
      const restored = restoreFormState(activeCaseId || null);
      if (restored) {
        console.log('Form state restored from localStorage (disbursements excluded)');
        // Clear the saved state after successful restoration
        clearSavedFormState();
      }
    }
  }, [initialData, isEditing, locationCaseId]);

  // Save form state when component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      // Only save if we're not in editing mode and there's no initialData
      if (!isEditing && !initialData) {
        saveFormState();
      }
    };
  }, [formData, selectedDisbursements, selectedTimesheets, selectedTimesheets2, expenseRows, disbursementRows, vatPercentage, officeExpensesPercentage, billingPeriod, expandedAccordion, isEditing, initialData]);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCreateNewTimesheet = () => {
    saveFormState();
    navigate("/timesheets/create", { state: { caseId: formData.case } });
  };

  useEffect(() => {
    console.log("location is", location);
  }, [location]);

  useEffect(() => {
    setSelectedTimesheets2(selectedTimesheets);
  }, [selectedTimesheets]);

  const handleTimesheetSelect = (timesheetId) => {
    const timesheet = timesheets.find(ts => ts._id === timesheetId);
    if (!timesheet) return;

    const isSelected = selectedTimesheets2.some(selectedTs => selectedTs._id === timesheetId);

    // Always allow toggling
    setSelectedTimesheets2(prev => {
      if (isSelected) {
        // Remove if it exists
        // return [...prev, timesheet];
        return prev.filter(ts => ts._id !== timesheetId);
      } else {
        // Add if it doesn't exist
        return [...prev, timesheet];
      }
    });

    const updatedItems = formData.items.map(item => {
      if (item.description === ITEM_KEYS.FEES) {
        // Use current selection intent instead of possibly stale item.timesheets
        if (isSelected) {
          // Deselect: subtract and remove
          return {
            ...item,
            value: Number(item.value || 0) - Number(timesheet.totalAmount || 0),
            timesheets: (item.timesheets || []).filter(t => t._id !== timesheetId)
          };
        }
        // Select: add and include
        return {
          ...item,
          value: Number(item.value || 0) + Number(timesheet.totalAmount || 0),
          timesheets: [
            ...(item.timesheets || []),
            timesheet
          ]
        };
      }
      return item;
    });

    // console.log("updated items", updatedItems);
    setFormData(prev => ({ ...prev, items: updatedItems }));
    // console.log("updated formdata ", formData);
  };

  const state = useSelector((state) => state);
  console.log("Redux State:", state);

  useEffect(() => {
    if (user) {
      setUserRole(user.role);
    }
  }, [user]);

  // Auto-populate "From" field for all users (including Managers)
  useEffect(() => {
    if (user && relationships?.length > 0 && !formData.from && !initialFrom && !fromFieldCleared) {
      // Find the user's relationship in the relationships array
      const userRelationship = relationships?.find(
        (rel) => rel._id === user.relationship && rel.type === t('common:filter_types.lawyer')
      );
      if (userRelationship) {
        setFormData(prev => ({ ...prev, from: userRelationship._id }));
      }
    }
  }, [user, relationships, formData.from, initialFrom, fromFieldCleared]);

  const typeOptions = [
    { label: t('forms:billing.types.invoice') },
    { label: t('forms:billing.types.credit') },
    { label: t('forms:billing.types.provision_note') },
  ];

  useEffect(() => {
    dispatch(fetchFiles());
    dispatch(fetchRelationships());
    dispatch(listDisbursements(listDisbursements()));

  }, [dispatch]);

  useEffect(() => {
    console.log("Dispatching listDisbursements...");
    dispatch(listDisbursements());
    console.log("Disbursement list......:", disbursements);
  }, [dispatch]);

  // Fetch timesheets for the current invoice context directly from the API,
  // instead of relying on the global Redux timesheet list.
  // This prevents stale or filtered Redux data from other screens (like Timesheets page)
  // from affecting the invoice and ensures filters are applied server-side.
  useEffect(() => {
    const fetchInvoiceTimesheets = async () => {
      if (!formData.case) {
        setTimesheets([]);
        return;
      }

      try {
        setTimesheetsLoading(true);

        const queryParams = new URLSearchParams();
        // Always scope to the active case for this invoice
        queryParams.append("fileCase", formData.case);

        // Note: We intentionally do NOT filter by owner here, so all timesheets
        // for the case are available regardless of who created them.
        // The "From" field on the invoice is for billing purposes, not for filtering timesheets.

        // Apply billing period filters when they are set
        if (billingPeriod[0] && billingPeriod[1]) {
          queryParams.append("startDate", billingPeriod[0].toISOString());
          queryParams.append("endDate", billingPeriod[1].toISOString());
        }

        const queryString = queryParams.toString();
        const url = queryString ? `/api/timesheets?${queryString}` : "/api/timesheets";

        const { data } = await axios.get(url);
        const fetched = Array.isArray(data) ? data : (data.timesheets || []);

        setTimesheets(fetched);
      } catch (error) {
        console.error("Failed to fetch invoice timesheets:", error);
        setTimesheets([]);
      } finally {
        setTimesheetsLoading(false);
      }
    };

    fetchInvoiceTimesheets();
    // We intentionally depend on the key invoice filters here so that
    // changing case or billing period always triggers a fresh backend fetch.
  }, [formData.case, billingPeriod, isEditing]);

  // Detect when returning from disbursement creation and auto-select the new disbursement
  // Only run this if we're creating a new invoice (not editing) and not on initial load
  useEffect(() => {
    // Skip if editing or if no case is selected yet
    if (isEditing || !formData.case) {
      return;
    }

    const lastCreatedDisbursementId = sessionStorage.getItem('lastCreatedDisbursementId');
    const creationTimestamp = sessionStorage.getItem('disbursementCreationTimestamp');
    
    if (lastCreatedDisbursementId && creationTimestamp && disbursements && disbursements.length > 0) {
      // Check if the creation was recent (within last 10 seconds)
      const timestamp = parseInt(creationTimestamp, 10);
      const now = Date.now();
      const timeDiff = now - timestamp;
      
      // Only auto-select if:
      // 1. Creation was recent (within 10 seconds)
      // 2. The disbursement exists and matches the current case
      // 3. It's not already selected
      if (timeDiff < 10000 && timeDiff > 0) {
        const newDisbursement = disbursements.find(disb => 
          disb._id === lastCreatedDisbursementId && 
          disb.case?._id === formData.case
        );
        
        if (newDisbursement && !selectedDisbursements.some(disb => disb._id === lastCreatedDisbursementId)) {
          // Auto-select the newly created disbursement
          console.log('Auto-selecting newly created disbursement:', newDisbursement);
          handleDisbursementSelect(newDisbursement._id);
          
          // Clear the session storage immediately after use
          sessionStorage.removeItem('lastCreatedDisbursementId');
          sessionStorage.removeItem('disbursementCreationTimestamp');
        } else {
          // Clear stale sessionStorage if disbursement not found or already selected
          sessionStorage.removeItem('lastCreatedDisbursementId');
          sessionStorage.removeItem('disbursementCreationTimestamp');
        }
      } else if (timeDiff >= 10000) {
        // Clear stale sessionStorage if timestamp is too old
        sessionStorage.removeItem('lastCreatedDisbursementId');
        sessionStorage.removeItem('disbursementCreationTimestamp');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disbursements, selectedDisbursements, formData.case, isEditing]);

  const [breadcrumbs, setBreadcrumbs] = useState([
    { label: t('pages:finance.breadcrumbs.home'), href: "/" },
    { label: t('pages:finance.breadcrumbs.finance'), href: "/finance" },
  ]);

  useEffect(() => {
    const locationCaseId = location.state?.caseId;
    console.log("locationCaseId", locationCaseId);
    const initialCaseFile =
      files?.find((file) => file._id === locationCaseId) || null;

    if (initialCaseFile) {
      setBreadcrumbs((prevBreadcrumbs) => [
        ...prevBreadcrumbs,
        {
          label: initialCaseFile.name || t('pages:finance.breadcrumbs.case_file'),
          href: `/files/${initialCaseFile._id}/finance`,
        },
      ]);
    }
  }, []);

  useEffect(() => {
    // console.log('Fetching sales document with ID:', id);
    if (isEditing && id) {
      // console.log('Fetching sales document with ID:', id);
      dispatch(getSalesDocument(id));
    }
  }, [isEditing, id, dispatch]);

  useEffect(() => {
    if (isEditing && salesDocument && salesDocument._id === id && timesheets.length > 0) {
      const processedData = JSON.parse(JSON.stringify(salesDocument));
      const feesItem = processedData.items.find(item => item.description === ITEM_KEYS.FEES);

      if (feesItem && feesItem.timesheets && feesItem.timesheets.length > 0) {
        // Extract timesheet IDs from the invoice
        const timesheetIds = feesItem.timesheets.map(ts =>
          typeof ts === 'object' && ts.$oid ? ts.$oid : (typeof ts === 'object' && ts._id ? ts._id : ts)
        );
        
        // Find full timesheet objects from the loaded timesheets array
        const fullTimesheetObjects = timesheetIds
          .map(id => timesheets.find(ts => ts._id === id))
          .filter(ts => ts !== undefined); // Filter out any that weren't found
        
        if (fullTimesheetObjects.length > 0) {
          setSelectedTimesheets2(fullTimesheetObjects);
          // Also store the IDs for comparison in submit handler
          setSelectedTimesheets(fullTimesheetObjects);
        }
      }
    }
  }, [isEditing, salesDocument, id, timesheets]);

  useEffect(() => {
    if (formData && formData.items) {
      const feesItem = formData.items.find(item => item.description === ITEM_KEYS.FEES);
      const feesValue = Number(feesItem?.value || 0);

      const officeExpenses = feesValue * (officeExpensesPercentage / 100);
      const VAT = feesValue * (vatPercentage / 100);

      const expensesTotal = expenseRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
      const disbursementsTotal = disbursementRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);

      const currentOfficeExpenses = formData.items.find(item =>
        item.description === ITEM_KEYS.OFFICE_EXPENSES_ON_FEES
      )?.value;
      const currentVAT = formData.items.find(item =>
        item.description === ITEM_KEYS.VAT
      )?.value;
      const currentExpenses = formData.items.find(item =>
        item.description === ITEM_KEYS.EXPENSES
      )?.value;
      const currentDisbursements = formData.items.find(item =>
        item.description === ITEM_KEYS.HIGH_DISBURSEMENT
      )?.value;

      if (
        Number(currentOfficeExpenses?.toFixed(2)) !== Number(officeExpenses.toFixed(2)) ||
        Number(currentVAT?.toFixed(2)) !== Number(VAT.toFixed(2)) ||
        Number(currentExpenses?.toFixed(2)) !== Number(expensesTotal.toFixed(2)) ||
        Number(currentDisbursements?.toFixed(2)) !== Number(disbursementsTotal.toFixed(2))
      ) {
        const updatedItems = formData.items.map(item => {
          if (item.description === ITEM_KEYS.OFFICE_EXPENSES_ON_FEES) {
            return { ...item, value: Number(officeExpenses.toFixed(2)) };
          }
          if (item.description === ITEM_KEYS.VAT) {
            return { ...item, value: Number(VAT.toFixed(2)) };
          }
          if (item.description === ITEM_KEYS.EXPENSES) {
            return { ...item, value: Number(expensesTotal.toFixed(2)) };
          }
          if (item.description === ITEM_KEYS.HIGH_DISBURSEMENT) {
            return { ...item, value: Number(disbursementsTotal.toFixed(2)) };
          }
          return item;
        });

        const total = updatedItems.reduce((sum, item) =>
          sum + Number(item.value || 0), 0
        );

        setFormData(prevFormData => ({
          ...prevFormData,
          items: updatedItems,
          total: Number(total.toFixed(2)),
          totalDue: Number(total.toFixed(2))
        }));
      }
    }
  }, [
    formData?.items?.find(item => item.description === ITEM_KEYS.FEES)?.value,
    expenseRows,
    disbursementRows,
    vatPercentage,
    officeExpensesPercentage
  ]);

  const { loading, success, error } = useSelector(
    (state) => state.salesDocumentCreate
  );

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (success) {
      setNotification({
        open: true,
        message: t('pages:finance.invoice_created'),
        severity: "success",
      });
      dispatch({ type: "SALESDOCUMENT_CREATE_RESET" }); // Reset the success state
      // Clear saved form state on successful submission
      clearSavedFormState();
      // Refresh sales documents list before navigating
      dispatch(getSalesDocuments());
      setTimeout(() => {
        // Navigate to finance page with refresh flag
        navigate("/finance", { state: { refresh: true } });
      }, 1000);
    } else if (error) {
      setNotification({
        open: true,
        message: error,
        severity: "error",
      });
      dispatch({ type: "SALESDOCUMENT_CREATE_RESET" }); // Reset the error state
    }
  }, [success, error, dispatch, navigate, t]);

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  useEffect(() => {
    if (formData.case) {
      const selectedFile = files.find(file => file._id === formData.case);
      const updatedItems = [...formData.items];
      const feesIdx = updatedItems.findIndex(i => i.description === ITEM_KEYS.FEES);
      if (feesIdx !== -1) {
        if (selectedFile?.rateConfig?.type === 'fixed') {
          // In fixed rate cases, use the fixed rate and ignore previously selected timesheets
          updatedItems[feesIdx] = {
            ...updatedItems[feesIdx],
            value: Number(selectedFile.rateConfig.fixedRate || 0),
            timesheets: [],
          };
          setSelectedTimesheets([]);
          setSelectedTimesheets2([]);
        } else {
          // In hourly cases, default to 0 until user selects timesheets
          const sumSelected = (selectedTimesheets2 || []).reduce((sum, ts) => sum + Number(ts?.totalAmount || 0), 0);
          updatedItems[feesIdx] = {
            ...updatedItems[feesIdx],
            value: Number(sumSelected.toFixed(2)),
          };
        }
        setFormData({
          ...formData,
          items: updatedItems
        });
      }
    }
  }, [formData.case, files]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCaseChange = async (event, newValue) => {
    setFormData({ ...formData, case: newValue?._id || "" });
    if (newValue) {
      const selectedFile = files.filter((file) => file._id === newValue._id)[0];
      const officeMembers = selectedFile.officeMembers;
      const officeMemberIds = selectedFile.officeMembers.map(
        (member) => member.member
      );
      const clients = selectedFile.parties.map((member) => member.name);
      const caseDisbursements = selectedFile.disbursements || [];
      const formattedDisbursements = caseDisbursements.map((disb) => ({
        ...disb,
        date: new Date(disb.date).toISOString().split("T")[0],
        amount: Number(disb.amount || 0),
        isEditable: false,
      }));

      setDisbursementRows(formattedDisbursements);


      setFilteredClients(clients);
      setFilteredRelationships(officeMemberIds);
    } else {
      setFilteredRelationships([]);
    }
  };

  const handleFromChange = (event, newValue) => {
    setFormData({ ...formData, from: newValue?._id || "" });
    // Track if field was manually cleared
    if (newValue === null) {
      setFromFieldCleared(true);
    } else {
      setFromFieldCleared(false);
    }
  };

  const handleToChange = (event, newValue) => {
    setFormData({ ...formData, to: newValue?._id || "" });
  };

  const handleTypeChange = (event, newValue) => {
    setFormData({ ...formData, type: newValue?.label || "" });
    let defaultItems = [];
    if (newValue?.label === t('forms:billing.types.invoice')) {
      defaultItems = [
        { description: ITEM_KEYS.FEES, value: 0.0 },
        { description: ITEM_KEYS.OFFICE_EXPENSES_ON_FEES, value: 0.0 },
        { description: ITEM_KEYS.EXPENSES, value: 0.0 },
        { description: ITEM_KEYS.VAT, value: 0.0 },
      ];
    } else if (newValue?.label === t('forms:billing.types.credit')) {
      defaultItems = [
        { description: ITEM_KEYS.CREDITS_ON_FEES, value: 0.0 },
        { description: ITEM_KEYS.CREDITS_ON_EXPENSES, value: 0.0 },
        { description: ITEM_KEYS.CREDITS_ON_PROVISIONS_ON_EXPENSES, value: 0.0 },
        { description: ITEM_KEYS.CREDITS_ON_DISBURSEMENTS_HIGH_VAT, value: 0.0 },
        { description: ITEM_KEYS.CREDITS_ON_PROVISIONS_ON_FEES, value: 0.0 },
        {
          description: ITEM_KEYS.CREDITS_ON_PROVISIONS_ON_DISBURSEMENTS_HIGH_VAT,
          value: 0.0,
        },
        { description: ITEM_KEYS.AMOUNT_SUBJECT_TO_HIGH_VAT, value: 0.0 },
        { description: ITEM_KEYS.CREDITS_ON_DISBURSEMENTS_LOW_VAT, value: 0.0 },
        {
          description: ITEM_KEYS.CREDITS_ON_PROVISIONS_ON_DISBURSEMENTS_LOW_VAT,
          value: 0.0,
        },
        { description: ITEM_KEYS.AMOUNT_SUBJECT_TO_LOW_VAT, value: 0.0 },
        { description: ITEM_KEYS.CREDITS_ON_DISBURSEMENTS_NO_VAT, value: 0.0 },
        {
          description: ITEM_KEYS.CREDITS_ON_PROVISIONS_ON_DISBURSEMENTS_NO_VAT,
          value: 0.0,
        },
      ];
    } else if (newValue?.label === t('forms:billing.types.provision_note')) {
      defaultItems = [
        { description: ITEM_KEYS.PROVISION_ON_FEES, value: 0.0 },
        { description: ITEM_KEYS.PROVISIONS_ON_EXPENSES, value: 0.0 },
        { description: ITEM_KEYS.PROVISIONS_ON_DISBURSEMENTS_HIGH_VAT, value: 0.0 },
        { description: ITEM_KEYS.AMOUNT_SUBJECT_TO_HIGH_VAT, value: 0.0 },
        { description: ITEM_KEYS.PROVISIONS_ON_DISBURSEMENTS_LOW_VAT, value: 0.0 },
        { description: ITEM_KEYS.AMOUNT_SUBJECT_TO_LOW_VAT, value: 0.0 },
        { description: ITEM_KEYS.PROVISIONS_ON_DISBURSEMENTS_NO_VAT, value: 0.0 },
        { description: ITEM_KEYS.AMOUNT_NOT_SUBJECT_TO_VAT, value: 0.0 },
      ];
    }
    setFormData({ ...formData, items: defaultItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const updatedItems = formData.items.map(item => {
        if (item.description === ITEM_KEYS.FEES) {
          return {
            ...item,
            timesheets: selectedTimesheets2.map(ts => ts._id), // Send only ObjectId references
          };
        }
        if (item.description === ITEM_KEYS.EXPENSES) {
          return {
            ...item,
            expenses: expenseRows.map(row => ({
              ...row,
              date: new Date(row.date).toISOString().split('T')[0], // Format date
              amount: Number(row.amount || 0),
              rate: Number(row.rate || 0),
              quantity: Number(row.quantity || 0),
              owner: formData.from, // Ensure owner is included
            })),
            value: expenseRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
          };
        }
        if (item.description === ITEM_KEYS.HIGH_DISBURSEMENT) {
          return {
            ...item,
            disbursements: selectedDisbursements
              .filter(disb => disb !== null) // Filter out null values
              .map(disb => ({
                ...disb,
                owner: disb.owner || formData.from, // Ensure owner is included
                date: disb.date ? new Date(disb.date).toISOString().split('T')[0] : null, // Format date
              })),
            value: selectedDisbursements.reduce((sum, disb) => sum + (Number(disb?.amount) || 0), 0),
          };
        }
        return item;
      });

      const recalculatedTotal = updatedItems.reduce(
        (sum, item) => sum + Number(item.value || 0),
        0
      );
      const vatAmount = Number(
        updatedItems.find(item => item.description === ITEM_KEYS.VAT)?.value || 0
      );

      // Convert type and status to lowercase to match the model's enum values
      const currentLanguage = ['nl', 'en'].includes((i18n.language || '').toLowerCase())
        ? i18n.language.toLowerCase()
        : 'en';

      const submitData = {
        ...formData,
        items: updatedItems,
        billingPeriod: formatBillingPeriod(billingPeriod),
        type: formData.type.toLowerCase(), // Convert to lowercase
        status: formData.status.toLowerCase(), // Convert to lowercase
        to: formData.to || null, // Ensure 'to' is not an empty string
        language: currentLanguage,
        total: Number(recalculatedTotal.toFixed(2)),
        totalDue: Number(recalculatedTotal.toFixed(2)),
        VAT: Number(vatAmount.toFixed(2)),
      };

      console.log("Submit Data:", JSON.stringify(submitData, null, 2));

      if (isEditing && onSubmit) {
        onSubmit(submitData); // Call the provided `onSubmit` function for editing
        
        // Get IDs of timesheets that were originally on this invoice
        const originalTimesheetIds = (selectedTimesheets || []).map(ts => 
          typeof ts === 'object' && ts._id ? ts._id : ts
        );
        
        // Get IDs of currently selected timesheets
        const currentTimesheetIds = (selectedTimesheets2 || []).map(ts => ts._id);
        
        // Find timesheets that were on the invoice but are now unchecked
        const uncheckedTimesheetIds = originalTimesheetIds.filter(id => 
          !currentTimesheetIds.includes(id)
        );
        
        // Mark all currently selected timesheets as billed
        if (selectedTimesheets2.length > 0) {
          dispatch(updateIsBilled(selectedTimesheets2.map(ts => ts._id), true));
        }
        
        // Mark timesheets that were removed from the invoice as unbilled
        if (uncheckedTimesheetIds.length > 0) {
          dispatch(updateIsBilled(uncheckedTimesheetIds, false));
        }

        // In the form submission section of CreateInvoicePage.js, add code to handle disbursements:

        // Add this after the timesheet handling code
        // Handle disbursements billing status
        const availableDisbursements = disbursements
          ?.filter((disb) => {
            return (
              disb.case &&
              disb.case._id === formData.case &&
              (!disb.isBilled || selectedDisbursements.some(selectedDisb => selectedDisb._id === disb._id)) &&
              matchesBillingPeriod(disb)
            );
          });

        // Find disbursements that were unchecked (available but not in selectedDisbursements)
        const uncheckedDisbursements = availableDisbursements.filter(disb =>
          !selectedDisbursements.some(selectedDisb => selectedDisb._id === disb._id)
        );

        // Update selected disbursements as billed (true) and link them to this invoice
        if (selectedDisbursements.length > 0 && initialData?._id) {
          dispatch(
            updateIsBilledDisbursements(
              selectedDisbursements.map(disb => disb._id),
              true,
              initialData._id
            )
          );
        }

        // Update unchecked disbursements as not billed (false) and unlink from this invoice
        if (uncheckedDisbursements.length > 0 && initialData?._id) {
          dispatch(
            updateIsBilledDisbursements(
              uncheckedDisbursements.map(disb => disb._id),
              false,
              initialData._id
            )
          );
        }

        dispatch(fetchFiles());
      } else {
        // Create a new invoice
        const created = await dispatch(createSalesDocument(submitData));

        // Mark linked timesheets as billed (true)
        if (selectedTimesheets2.length > 0) {
        dispatch(updateIsBilled(selectedTimesheets2, true));
        }

        // Link selected disbursements to the created invoice and mark as billed
        if (created?._id && selectedDisbursements.length > 0) {
          dispatch(
            updateIsBilledDisbursements(
              selectedDisbursements.map(disb => disb._id),
              true,
              created._id
            )
          );
        }

        dispatch(fetchFiles());
      }
    } catch (error) {
      setNotification({
        open: true,
        message: t('pages:finance.error_invoice') + ": " + error.message,
        severity: "error",
      });
    }
  };


  const buttonStyles = { margin: "0 8px" };

  const handleCancel = () => {
    // Clear saved form state when canceling
    clearSavedFormState();
    navigate("/finance");
  };

  const normalizeDate = (date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  };

  const matchesBillingPeriod = (timesheet) => {
    if (billingPeriod[0] && billingPeriod[1]) {
      const startDate = normalizeDate(billingPeriod[0]);
      const endDate = normalizeDate(billingPeriod[1]);
      const timesheetDate = normalizeDate(timesheet.date);
      return timesheetDate >= startDate && timesheetDate <= endDate;
    }
    return true;
  };

  const handleExpenseChange = (e, index) => {
    const { name, value } = e.target;
    const updatedRows = [...expenseRows];
    updatedRows[index] = {
      ...updatedRows[index],
      [name]: value,
    };

    if (name === 'type') {
      updatedRows[index].quantity = value === EXPENSE_TYPES.KILOMETER ? 1 : 0;
      updatedRows[index].rate = 0;
      updatedRows[index].amount = 0;
    } else if (updatedRows[index].type === EXPENSE_TYPES.KILOMETER) {
      if (name === 'quantity' || name === 'rate') {
        const quantity = parseFloat(updatedRows[index].quantity) || 0;
        const rate = parseFloat(updatedRows[index].rate) || 0;
        updatedRows[index].amount = quantity * rate;
      }
    } else {
      if (name === 'rate') {
        updatedRows[index].amount = parseFloat(value) || 0;
      }
    }

    setExpenseRows(updatedRows);
  };

  const handleDisbursementChange = (e, index) => {
    const { name, value } = e.target;
    const updatedRows = [...disbursementRows];
    updatedRows[index] = {
      ...updatedRows[index],
      [name]: value,
    };

    if (name === 'amount') {
      updatedRows[index].amount = parseFloat(value) || 0;
    }

    setDisbursementRows(updatedRows);
  };

  const handleAddExpenseRow = () => {
    setExpenseRows(prev => [
      ...prev,
      {
        date: getCurrentDate(),
        owner: formData.from || "",
        type: EXPENSE_TYPES.KILOMETER,
        description: "",
        quantity: 1,
        rate: 0.0,
        amount: 0.0,
        isEditable: true,
      }
    ]);
  };

  const handleAddDisbursementRow = () => {
    setDisbursementRows(prev => [
      ...prev,
      {
        date: getCurrentDate(),
        owner: formData.from || "",
        type: EXPENSE_TYPES.KILOMETER,
        description: "",
        quantity: 1,
        rate: 0.0,
        amount: 0.0,
        isEditable: true,
      }
    ]);
  };

  const handleSaveExpense = (index) => {
    const updatedRows = expenseRows.map((row, i) => {
      if (i === index) {
        return {
          ...row,
          isEditable: false,
          amount: Number(row.amount || 0),
          rate: Number(row.rate || 0),
          quantity: Number(row.quantity || 0)
        };
      }
      return row;
    });
    setExpenseRows(updatedRows);

    const updatedItems = formData.items.map(item => {
      if (item.description === ITEM_KEYS.EXPENSES) {
        return {
          ...item,
          expenses: updatedRows,
          value: updatedRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0)
        };
      }
      return item;
    });

    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const handleSaveDisbursement = (index) => {
    const updatedRows = disbursementRows.map((row, i) => {
      if (i === index) {
        return {
          ...row,
          isEditable: false,
          amount: Number(row.amount || 0)
        };
      }
      return row;
    });
    setDisbursementRows(updatedRows);

    const updatedItems = formData.items.map(item => {
      if (item.description === ITEM_KEYS.HIGH_DISBURSEMENT) {
        return {
          ...item,
          disbursements: updatedRows,
          value: updatedRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0)
        };
      }
      return item;
    });

    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const handleEditExpense = (index) => {
    const updated = [...expenseRows];
    updated[index].isEditable = true;
    setExpenseRows(updated);
  };

  const handleEditDisbursement = (index) => {
    const updated = [...disbursementRows];
    updated[index].isEditable = true;
    setDisbursementRows(updated);
  };

  const handleRemoveExpenseRow = (index) => {
    const updatedRows = expenseRows.filter((_, i) => i !== index);
    setExpenseRows(updatedRows);

    const updatedItems = formData.items.map(item => {
      if (item.description === ITEM_KEYS.EXPENSES) {
        return {
          ...item,
          expenses: updatedRows,
          value: updatedRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0)
        };
      }
      return item;
    });

    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const handleRemoveDisbursementRow = (index) => {
    const updatedRows = disbursementRows.filter((_, i) => i !== index);
    setDisbursementRows(updatedRows);

    const updatedItems = formData.items.map(item => {
      if (item.description === ITEM_KEYS.HIGH_DISBURSEMENT) {
        return {
          ...item,
          disbursements: updatedRows,
          value: updatedRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0)
        };
      }
      return item;
    });

    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const handleAccordianClick = (panel) => {
    setExpandedAccordion(expandedAccordion === panel ? null : panel); // Toggle logic
  };

  const [filteredTimesheets, setFilteredTimesheets] = useState([]);

  // Keep a derived list of timesheets for the current case.
  // We intentionally do NOT filter on billing state here, so the user can see
  // all timesheets for the matter (billed or not). Billing flags are handled
  // explicitly on submit.
  useEffect(() => {
    if (formData.case) {
      const newFiltered = (timesheets || []).filter((ts) => {
        const sameCase = ts.fileCase && ts.fileCase._id === formData.case;
        if (!sameCase) return false;
        return true;
      });

      setFilteredTimesheets(newFiltered);
    } else {
      setFilteredTimesheets([]);
    }
  }, [formData.case, timesheets]);

  useEffect(() => {
    if (formData.case) {
      const selectedFile = files?.find((file) => file._id === formData.case);
      if (selectedFile) {
        const officeMembers = selectedFile.officeMembers.map(
          (member) => member.member
        );
        const lawyers = relationships?.filter(rel =>
          officeMembers.some(member => member._id === rel._id)
        );
        const clients = selectedFile.parties.map((member) => member.name);
        setFilteredRelationships(lawyers);
        setFilteredClients(clients);
      }
    }
  }, [formData.case, files, relationships]);

  useEffect(() => {
    if (formData.items) {
      const updatedItems = formData.items.map(item => {
        if (item.description === ITEM_KEYS.EXPENSES) {
          return {
            ...item,
            expenses: expenseRows,
            value: expenseRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0)
          };
        }
        if (item.description === ITEM_KEYS.HIGH_DISBURSEMENT) {
          return {
            ...item,
            disbursements: disbursementRows,
            value: disbursementRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0)
          };
        }
        return item;
      });

      const newTotal = updatedItems.reduce((sum, item) => sum + Number(item.value || 0), 0);

      setFormData(prev => ({
        ...prev,
        items: updatedItems,
        total: newTotal,
        totalDue: newTotal
      }));
    }
  }, [expenseRows, disbursementRows]);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (initialData && isFirstRender.current) {
      isFirstRender.current = false;

      const processedData = JSON.parse(JSON.stringify(initialData));

      const feesItem = processedData.items.find(item => item.description === ITEM_KEYS.FEES);
      const expensesItem = processedData.items.find(item => item.description === ITEM_KEYS.EXPENSES);
      const disbursementsItem = processedData.items.find(item => item.description === ITEM_KEYS.HIGH_DISBURSEMENT);

      // Match timesheet IDs with full objects from the loaded timesheets array
      if (feesItem?.timesheets && feesItem.timesheets.length > 0 && timesheets.length > 0) {
        const timesheetIds = feesItem.timesheets.map(ts =>
          typeof ts === 'object' && ts.$oid ? ts.$oid : (typeof ts === 'object' && ts._id ? ts._id : ts)
        );
        const fullTimesheetObjects = timesheetIds
          .map(id => timesheets.find(ts => ts._id === id))
          .filter(ts => ts !== undefined);
        setSelectedTimesheets(fullTimesheetObjects);
      } else {
        setSelectedTimesheets(feesItem?.timesheets || []);
      }

      if (expensesItem?.expenses?.length > 0) {
        const formattedExpenses = expensesItem.expenses.map(exp => ({
          ...exp,
          date: new Date(exp.date).toISOString().split('T')[0],
          amount: Number(exp.amount || 0),
          rate: Number(exp.rate || 0),
          quantity: Number(exp.quantity || 0),
          isEditable: false
        }));
        setExpenseRows(formattedExpenses);
      }

      // if (disbursementsItem?.disbursements?.length > 0) {
      //   const formattedDisbursements = disbursementsItem.disbursements.map(disb => ({
      //     ...disb,
      //     date: new Date(disb.date).toISOString().split('T')[0],
      //     amount: Number(disb.amount || 0),
      //     isEditable: false
      //   }));
      //   setDisbursementRows(formattedDisbursements);
      // }

      if (initialData) {
        const disbursementsItem = initialData.items.find(
          (item) => item.description === ITEM_KEYS.HIGH_DISBURSEMENT
        );

        if (disbursementsItem?.disbursements?.length > 0) {
          const formattedDisbursements = disbursementsItem.disbursements.map((disb) => ({
            ...disb,
            date: new Date(disb.date).toISOString().split("T")[0],
            amount: Number(disb.amount || 0),
            isEditable: false,
          }));
          setDisbursementRows(formattedDisbursements);
        }
      }

      setFormData(prevData => ({
        ...prevData,
        ...processedData,
        items: processedData.items.map(item => ({
          ...item,
          value: Number(item.value || 0)
        }))
      }));


    }
  }, [initialData]);

  // When timesheets load and we have initialData, match timesheet IDs with full objects
  useEffect(() => {
    if (initialData && timesheets.length > 0 && isFirstRender.current === false) {
      const processedData = JSON.parse(JSON.stringify(initialData));
      const feesItem = processedData.items.find(item => item.description === ITEM_KEYS.FEES);
      
      if (feesItem?.timesheets && feesItem.timesheets.length > 0) {
        const timesheetIds = feesItem.timesheets.map(ts =>
          typeof ts === 'object' && ts.$oid ? ts.$oid : (typeof ts === 'object' && ts._id ? ts._id : ts)
        );
        const fullTimesheetObjects = timesheetIds
          .map(id => timesheets.find(ts => ts._id === id))
          .filter(ts => ts !== undefined);
        
        if (fullTimesheetObjects.length > 0) {
          setSelectedTimesheets(fullTimesheetObjects);
        }
      }
    }
  }, [initialData, timesheets]);

  useEffect(() => {
    if (formData?.items) {
      const updatedItems = formData.items.map(item => {
        if (item.description === ITEM_KEYS.EXPENSES) {
          const expenseTotal = expenseRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
          return {
            ...item,
            expenses: expenseRows,
            value: expenseTotal
          };
        }
        if (item.description === ITEM_KEYS.HIGH_DISBURSEMENT) {
          const disbursementTotal = disbursementRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
          return {
            ...item,
            disbursements: disbursementRows,
            value: disbursementTotal
          };
        }
        return item;
      });

      const newTotal = updatedItems.reduce((sum, item) => sum + Number(item.value || 0), 0);
      const feesValue = updatedItems.find(item => item.description === ITEM_KEYS.FEES)?.value || 0;
      const newVAT = feesValue * 0.21;

      setFormData(prev => ({
        ...prev,
        items: updatedItems,
        total: newTotal,
        totalDue: newTotal,
        VAT: newVAT
      }));
    }
  }, [expenseRows, disbursementRows]);


  useEffect(() => {
    if (initialData) {
      const disbursementsItem = initialData.items.find(item => item.description === ITEM_KEYS.HIGH_DISBURSEMENT);
      if (disbursementsItem?.disbursements?.length > 0) {
        const formattedDisbursements = disbursementsItem.disbursements.map(disb => ({
          ...disb,
          date: new Date(disb.date).toISOString().split('T')[0],
          amount: Number(disb.amount || 0),
          isEditable: false,
        }));
        setDisbursementRows(formattedDisbursements);
      }
    }
  }, [initialData]);

  const handleSelectAllDisbursements = () => {
    const allDisbursements = disbursements.filter(
      (disb) => disb?.case?._id === formData.case && !disb.isBilled && matchesBillingPeriod(disb)
    );

    const isAllSelected = allDisbursements.every(disb =>
      selectedDisbursements.some(selectedDisb => selectedDisb._id === disb._id)
    );

    const updatedSelectedDisbursements = isAllSelected
      ? [] // Deselect all if already selected
      : allDisbursements; // Select all disbursements

    setSelectedDisbursements(updatedSelectedDisbursements);

    // Update the High Disbursement item in formData
    const updatedItems = formData.items?.map(item => {
      if (item.description === ITEM_KEYS.HIGH_DISBURSEMENT) {
        return {
          ...item,
          disbursements: updatedSelectedDisbursements,
          value: updatedSelectedDisbursements.reduce(
            (sum, disb) => sum + (Number(disb.amount) || 0),
            0
          ),
        };
      }
      return item;
    });

    // Recalculate total and totalDue
    const newTotal = updatedItems.reduce((sum, item) => sum + Number(item.value || 0), 0);

    setFormData(prev => ({
      ...prev,
      items: updatedItems,
      total: newTotal,
      totalDue: newTotal,
    }));
  };

  const handleSelectAllTimesheets = () => {
    const allTimesheets = (timesheets || []).filter((ts) => {
      const sameCase = ts.fileCase?._id === formData.case;
      if (!sameCase) return false;

      // For "select all", respect the billing period only, not billing status.
      return matchesBillingPeriod(ts);
    });

    const mergedTimesheets = [
      ...selectedTimesheets,
      ...(allTimesheets?.filter(
        ts => !selectedTimesheets2.some(selectedTs => selectedTs._id === ts._id)
      ) || []),
    ];



    // Merge already selected timesheets with the new ones
    if (selectedTimesheets.length > 0) {

      setSelectedTimesheets2(mergedTimesheets);

    } else {

      setSelectedTimesheets2(allTimesheets || []);
    }

    const updatedItems = formData.items.map(item => {
      if (item.description === ITEM_KEYS.FEES) {
        const totalAmount = allTimesheets.reduce((sum, ts) => sum + Number(ts.totalAmount), 0);
        return {
          ...item,
          value: totalAmount,
          timesheets: allTimesheets,
        };
      }
      return item;
    });

    setFormData(prev => ({
      ...prev,
      items: updatedItems,
    }));
  };


  const handleDisbursementSelect = (disbursementId) => {
    const disbursement = disbursements?.find(disb => disb._id === disbursementId);
    if (!disbursement) return;

    const isSelected = selectedDisbursements.some(selectedDisb => selectedDisb._id === disbursementId);

    const updatedSelectedDisbursements = isSelected
      ? selectedDisbursements.filter(disb => disb._id !== disbursementId)
      : [...selectedDisbursements, disbursement];

    setSelectedDisbursements(updatedSelectedDisbursements);

    // Update the High Disbursement item in formData
    const updatedItems = formData.items?.map(item => {
      if (item.description === ITEM_KEYS.HIGH_DISBURSEMENT) {
        return {
          ...item,
          disbursements: updatedSelectedDisbursements,
          value: updatedSelectedDisbursements.reduce((sum, disb) => sum + (Number(disb.amount) || 0), 0),
        };
      }
      return item;
    });

    const newTotal = updatedItems.reduce((sum, item) => sum + Number(item.value || 0), 0);


    // setFormData(prev => ({
    //   ...prev,
    //   items: updatedItems,
    // }));
    setFormData(prev => ({
      ...prev,
      items: updatedItems,
      total: newTotal,
      totalDue: newTotal,
    }));

  };


  // const handleSelectAllDisbursements = () => {
  //   if (selectedDisbursements.length === disbursementRows.length) {
  //     // Deselect all if all are already selected
  //     setSelectedDisbursements([]);
  //   } else {
  //     // Select all disbursements
  //     setSelectedDisbursements(disbursementRows);
  //   }
  // };


  const filteredDisbursements = disbursements.filter(
    (disb) => disb?.case?._id === formData.case
  );
  console.log("disbursemnt list", disbursements)

  // Helper to format billing period as string
  const formatBillingPeriod = (periodArr) => {
    if (!periodArr || !periodArr[0] || !periodArr[1]) return "";
    const start = new Date(periodArr[0]);
    const end = new Date(periodArr[1]);
    return `${start.toLocaleDateString('en-GB')} - ${end.toLocaleDateString('en-GB')}`;
  };

  // Helper to parse billing period string to [Date, Date]
  const parseBillingPeriod = (periodStr) => {
    if (!periodStr || typeof periodStr !== 'string' || !periodStr.includes(' - ')) return [null, null];
    const [start, end] = periodStr.split(' - ').map(d => {
      const [day, month, year] = d.split('/');
      return new Date(`${year}-${month}-${day}`);
    });
    return [start, end];
  };

  // When loading initialData (edit mode), set billingPeriod if present
  useEffect(() => {
    if (initialData && initialData.billingPeriod) {
      setBillingPeriod(parseBillingPeriod(initialData.billingPeriod));
    }
  }, [initialData]);

  useEffect(() => {
    if (isEditing && salesDocument && salesDocument._id === id && salesDocument.billingPeriod) {
      setBillingPeriod(parseBillingPeriod(salesDocument.billingPeriod));
    }
  }, [isEditing, salesDocument, id]);

  return (
    <Container
      maxWidth="lg"
      style={{ position: "relative", paddingBottom: "80px" }}
    >
      <BreadcrumbNav
        title={isEditing ? t('pages:finance.edit_invoice') : t('pages:finance.create_invoice')}
        breadcrumbs={breadcrumbs}
        showBreadcrumbs={true}
      />

      <Box
        sx={{
          backgroundColor: "white",
          padding: "30px 24px",
          marginTop: "24px",
        }}
      >
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <FileAutocomplete
                options={files}
                value={files?.find((file) => file._id === formData.case) || null}
                onChange={handleCaseChange}
                label={t('common:case')}
                name="case"
              />
            </Grid>
            <Grid item xs={4}>
              <CustomAutocomplete
                options={filteredRelationships}
                value={
                  filteredRelationships?.find(
                    (rel) => rel._id === formData.from
                  ) || null
                }
                onChange={handleFromChange}
                label={t('common:from')}
                filterType={t('common:filter_types.member')}
                addNewLabel={t('common:add_from')}
                addNewPath="/relationships/create"
              />
            </Grid>

            <Grid item xs={4}>
              <CustomAutocomplete
                options={filteredClients}
                value={
                  filteredClients?.find(
                    (client) => client._id === formData.to
                  ) || null
                }
                onChange={handleToChange}
                label={t('common:to')}
                filterType={t('common:filter_types.party')}
                addNewLabel={t('common:add_to')}
                addNewPath="/relationships/create"
              />
            </Grid>

            {showAutocomplete && (
              <Grid item xs={4}>
                <Autocomplete
                  options={typeOptions}
                  getOptionLabel={(option) => option.label}
                  value={
                    typeOptions.find(
                      (option) => option.label === formData.type
                    ) || null
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('common:type')}
                      variant="standard"
                      fullWidth
                      required
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={4}>
              {/* <TextField
                type="date"
                label="Date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                fullWidth
                required
                sx={textFieldStyles({ width: "100%" })}
                size="small"
                InputLabelProps={{ shrink: true }}
              /> */}
              <MUIDatePicker
                label={t('forms:labels.date')}
                value={formData.date}
                // value={formData.date ? format(new Date(formData.date), 'yyyy-MM-dd') : ''}
                onChange={(newValue) => setFormData({ ...formData, date: newValue })}
                sx={textFieldStyles({ width: "100%" })}
              />
            </Grid>
            <Grid item xs={4}>
              {/* <TextField
                type="date"
                label="Due Date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                fullWidth
                required
                sx={textFieldStyles({ width: "100%" })}
                size="small"
                InputLabelProps={{ shrink: true }}
              /> */}
              <MUIDatePicker
                label={t('forms:labels.dueDate')}
                value={formData.dueDate}
                onChange={(newValue) => setFormData({ ...formData, dueDate: newValue })}
                sx={textFieldStyles({ width: "100%" })}
              />
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ width: "100%", textAlign: "center" }}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                  <DateRangePicker
                    startText={t('common:start_date')}
                    endText={t('common:end_date')}
                    size="small"
                    inputFormat="dd/MM/yyyy HH:mm"
                    slots={{ field: SingleInputDateRangeField }}
                    slotProps={{
                      textField: {
                        InputProps: { endAdornment: <Calendar /> },
                        fullWidth: true,
                        sx: textFieldStyles({ width: "100%" }),
                        size: "small",
                        label: t('forms:select_billing_period'),
                      },
                    }}
                    value={billingPeriod}
                    onOpen={() => setIsPickerOpen(true)}
                    onClose={() => setIsPickerOpen(false)}
                    onChange={(newValue) => {
                      // If start date is selected but end date is not, automatically set end date to 14 days after start date
                      if (newValue && newValue[0] && !newValue[1]) {
                        const startDate = new Date(newValue[0]);
                        const endDate = new Date(startDate);
                        endDate.setDate(startDate.getDate() + 14);
                        setBillingPeriod([newValue[0], endDate]);
                      } else {
                        setBillingPeriod(newValue);
                      }
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <div>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography variant="h6" sx={tableStyles.typography}>
                    {t('pages:invoices.billing_items')}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start", // Aligns to the start
                      cursor: "pointer",
                      color: "rgb(0, 91, 146)", // Set text and icon color
                    }}
                    onClick={handleMenuClick}
                  >
                    <AddIcon sx={{ fontSize: "0.9rem", marginRight: "4px", color: "inherit" }} />
                    <Typography
                      variant="h6"
                      sx={{
                        ...tableStyles.typography,
                        marginBottom: "0px",

                        color: "inherit", // Ensures text color matches
                      }}
                    >
                      {t('buttons:primary.add')}
                    </Typography>
                    <ArrowDropDownIcon sx={{ fontSize: "1.2rem", marginLeft: "4px", color: "inherit", marginTop: "-2px" }} />
                  </Box>

                  <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
                    <MenuItem
                      onClick={() => handleAccordianClick('fees')}
                      sx={{ ...tableStyles.typography, fontWeight: "normal" }}
                    >
                      {t('pages:invoices.add_menu.fees')}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleAddExpenseRow();
                        handleMenuClose(); // Close menu
                        setExpandedAccordion('expenses'); // Open expenses accordion
                      }}
                      sx={{ ...tableStyles.typography, fontWeight: "normal" }}
                      disabled={expenseRows.some(row => row.isEditable)}
                    >
                      {t('pages:invoices.add_menu.expenses')}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleAddDisbursementRow();
                        handleMenuClose(); // Close menu
                        setExpandedAccordion('highDisbursement'); // Open disbursements accordion
                      }}
                      sx={{ ...tableStyles.typography, fontWeight: "normal" }}
                      disabled={disbursementRows.some(row => row.isEditable)}
                    >
                      {t('pages:invoices.add_menu.high_disbursement')}s
                    </MenuItem>
                  </Menu>

                </Box>

                <TableContainer component={Paper} sx={tableStyles.container}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell sx={tableStyles.headerCell}>
                          {t('forms:labels.description')}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tableStyles.headerCell,
                            width: "100px",
                          }}
                        >
                          {/* Empty header for VAT Rate */}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tableStyles.headerCell,
                            width: "100px",
                          }}
                        >
                          Value (€)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.items?.map((item, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            "&:hover": {
                              backgroundColor: "white",
                              transition: "background-color 0.1s ease",
                            },
                          }}
                        >
                          <TableCell sx={tableStyles.cell}>
                            {item.description === ITEM_KEYS.FEES ? (
                              <>
                                {files?.find(f => f._id === formData.case)?.rateConfig?.type === 'fixed' ? (
                                  <Typography sx={{ fontSize: "10.5px" }}>{getTranslatedDescription(ITEM_KEYS.FEES)}</Typography>
                                ) : (
                                  <Accordion
                                    sx={{
                                      boxShadow: "none",
                                      padding: "0px",
                                      margin: "0px",
                                      "&:before": {
                                        display: "none",
                                      },
                                      "&:hover": {
                                        backgroundColor: "white",
                                      },
                                      "& .MuiAccordionSummary-root": {
                                        flexDirection: "row-reverse",
                                      },
                                      "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                                        transform: "rotate(90deg)",
                                      },
                                      "& .MuiAccordionSummary-content": {
                                        margin: "0",
                                        padding: "0",
                                        minHeight: "0",
                                      },
                                      "& .MuiAccordionSummary-content.Mui-expanded": {
                                        margin: "5px 0",
                                      },
                                      "& .MuiAccordionSummary-expandIconWrapper": {
                                        marginRight: "6px",
                                      },
                                      fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
                                    }}
                                    expanded={expandedAccordion === 'fees'} // Check if the current accordion is expanded
                                    onChange={() => handleAccordianClick('fees')} // Toggle the accordion
                                  >
                                    <AccordionSummary
                                      expandIcon={
                                        <ArrowForwardIosSharpIcon
                                          sx={{
                                            fontSize: "10.5px",
                                            padding: "0",
                                            margin: "0",
                                          }}
                                        />
                                      }
                                      aria-controls="panel1a-content"
                                      id="panel1a-header"
                                      sx={{
                                        padding: "0px",
                                        margin: "0px",
                                        minHeight: "0px",
                                        "&.MuiAccordionSummary-root": {
                                          minHeight: "0px",
                                        },
                                      }}
                                    >
                                      <Typography sx={{ fontSize: "10.5px", fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif", }}>{getTranslatedDescription(ITEM_KEYS.FEES)}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails
                                      sx={{
                                        marginTop: 0,
                                        padding: "8px 16px 26px",
                                        fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
                                      }}
                                    >
                                      {timesheets?.filter(
                                        (ts) =>
                                          ts.fileCase?._id === formData.case &&
                                          matchesBillingPeriod(ts)
                                      ).length >= 0 ? (
                                        <Box sx={{ fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif", }}>
                                          <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            sx={{ fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif", }}
                                          >
                                            {timesheets?.filter(ts =>
                                              ts.fileCase?._id === formData.case &&
                                              matchesBillingPeriod(ts)
                                            ).length === 0
                                              ? t('messages:info.no_timesheets_found', 'No timesheets found for this case.')
                                              : t('messages:info.add_more_timesheets', 'Add more timesheets if needed.')
                                            }
                                          </Typography>
                                          <Box sx={{ 
                                            display: 'flex', 
                                            gap: 1, 
                                            alignItems: 'center',
                                            mt: 1,
                                            mb: 1
                                          }}>
                                            {timesheets?.filter(ts =>
                                              ts.fileCase?._id === formData.case &&
                                              matchesBillingPeriod(ts)
                                            ).length > 0 && (
                                              <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => handleSelectAllTimesheets()}
                                                sx={{
                                                  fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
                                                  fontSize: '0.75rem',
                                                  textTransform: 'none',
                                                  borderColor: '#e0e0e0',
                                                  color: '#666',
                                                  '&:hover': {
                                                    borderColor: '#1976d2',
                                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                  },
                                                  minWidth: 'auto',
                                                  px: 2,
                                                  py: 0.5
                                                }}
                                              >
                                                {t('buttons:confirmation.select_all', 'Select All')}
                                              </Button>
                                            )}
                                            <Button
                                              variant="outlined"
                                              size="small"
                                              startIcon={<AddIcon sx={{ fontSize: '0.875rem' }} />}
                                              onClick={handleCreateNewTimesheet}
                                              sx={{
                                                fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
                                                fontSize: '0.75rem',
                                                textTransform: 'none',
                                                borderColor: '#1976d2',
                                                color: '#1976d2',
                                                '&:hover': {
                                                  borderColor: '#1565c0',
                                                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                  color: '#1565c0'
                                                },
                                                minWidth: 'auto',
                                                px: 2,
                                                py: 0.5
                                              }}
                                            >
                                              {t('pages:timesheets.create_new')}
                                            </Button>
                                          </Box>

                                        </Box>
                                      ) : null}
                                      <Table sx={{ fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif", }}>
                                        <TableHead>
                                          <TableRow>
                                            <TableCell
                                              sx={{
                                                fontWeight: "bold",
                                                color: "#333",
                                                padding: "0px",
                                              }}
                                            >
                                                {t('buttons:secondary.select')}
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontWeight: "bold",
                                                color: "#333",
                                                padding: "0px",
                                              }}
                                            >
                                                {t('forms:labels.type')}
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontWeight: "bold",
                                                color: "#333",
                                                padding: "0px",
                                                width: "150px",
                                              }}
                                            >
                                              {t('forms:billing.table_headers.date')}
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontWeight: "bold",
                                                color: "#333",
                                                padding: "0px",
                                                width: "150px",
                                              }}
                                            >
                                                {t('forms:billing.table_headers.amount')} (€)
                                            </TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {timesheets
                                            ?.filter((ts) => {
                                              // Show all timesheets for the case, regardless of billing status
                                              return (
                                                ts.fileCase &&
                                                ts.fileCase._id === formData.case &&
                                                matchesBillingPeriod(ts)
                                              );
                                            })
                                            .flatMap((timesheet, timesheetIndex) => {
                                              // If timesheet has billingItems, show each billingItem as a separate row
                                              // This includes travel expenses (travel_time, kilometers)
                                              if (timesheet.billingItems && Array.isArray(timesheet.billingItems) && timesheet.billingItems.length > 0) {
                                                return timesheet.billingItems.map((billingItem, itemIndex) => {
                                                  // Use timesheet.type for the code (e.g., "COCL", "COVE")
                                                  // For travel expenses, show translated labels
                                                  let displayType = timesheet.type || '';
                                                  if (billingItem.type === 'travel_time') {
                                                    displayType = t('common:travel_time_display');
                                                  } else if (billingItem.type === 'kilometers') {
                                                    displayType = t('common:travel_cost');
                                                  }
                                                  // For working_time, use the timesheet.type (which contains the code)
                                                  
                                                  return (
                                                    <TableRow
                                                      key={`${timesheet._id}-${itemIndex}`}
                                                      sx={{
                                                        "&:hover": {
                                                          backgroundColor: "#f5f5f5",
                                                        },
                                                      }}
                                                    >
                                                      <TableCell
                                                        sx={{
                                                          padding: "0 8px",
                                                        }}
                                                      >
                                                        {itemIndex === 0 && (
                                                          <Checkbox
                                                            checked={selectedTimesheets2.some(selectedTs => selectedTs._id === timesheet._id)}
                                                            disabled={false}
                                                            sx={{
                                                              "&.MuiCheckbox-root": {
                                                                padding: "5px",
                                                              },
                                                            }}
                                                            onChange={() => handleTimesheetSelect(timesheet._id)}
                                                          />
                                                        )}
                                                      </TableCell>
                                                      <TableCell
                                                        sx={{
                                                          padding: "0 8px",
                                                        }}
                                                      >
                                                        {displayType}
                                                      </TableCell>
                                                      <TableCell
                                                        sx={{
                                                          padding: "0 8px",
                                                        }}
                                                      >
                                                        {itemIndex === 0 ? new Date(timesheet.date).toLocaleDateString() : ''}
                                                      </TableCell>
                                                      <TableCell
                                                        sx={{
                                                          padding: "0 8px",
                                                        }}
                                                      >
                                                        {itemIndex === 0 ? (
                                                          <>€{" "}{timesheet.totalAmount.toFixed(2)}</>
                                                        ) : (
                                                          <>€{" "}{
                                                            billingItem.type === 'kilometers'
                                                              ? (parseFloat(billingItem.quantity || 0) * (billingItem.rate || 0)).toFixed(2)
                                                              : (() => {
                                                                  const duration = billingItem.quantity || '00:00:00';
                                                                  const [hours, minutes, seconds] = duration.split(':').map(Number);
                                                                  const totalHours = hours + (minutes / 60) + (seconds / 3600);
                                                                  return (totalHours * (billingItem.rate || 0)).toFixed(2);
                                                                })()
                                                          }</>
                                                        )}
                                                      </TableCell>
                                                    </TableRow>
                                                  );
                                                });
                                              } else {
                                                // Fallback: show main timesheet entry if no billingItems
                                                // Use timesheet.type which contains the code (e.g., "COCL", "COVE")
                                                return (
                                                  <TableRow
                                                    key={timesheet._id}
                                                    sx={{
                                                      "&:hover": {
                                                        backgroundColor: "#f5f5f5",
                                                      },
                                                    }}
                                                  >
                                                    <TableCell
                                                      sx={{
                                                        padding: "0 8px",
                                                      }}
                                                    >
                                                      <Checkbox
                                                        checked={selectedTimesheets2.some(selectedTs => selectedTs._id === timesheet._id)}
                                                        disabled={false}
                                                        sx={{
                                                          "&.MuiCheckbox-root": {
                                                            padding: "5px",
                                                          },
                                                        }}
                                                        onChange={() => handleTimesheetSelect(timesheet._id)}
                                                      />
                                                    </TableCell>
                                                    <TableCell
                                                      sx={{
                                                        padding: "0 8px",
                                                      }}
                                                    >
                                                      {timesheet.type || ''}
                                                    </TableCell>
                                                    <TableCell
                                                      sx={{
                                                        padding: "0 8px",
                                                      }}
                                                    >
                                                      {new Date(timesheet.date).toLocaleDateString()}
                                                    </TableCell>
                                                    <TableCell
                                                      sx={{
                                                        padding: "0 8px",
                                                      }}
                                                    >
                                                      €{" "}
                                                      {timesheet.totalAmount.toFixed(2)}
                                                    </TableCell>
                                                  </TableRow>
                                                );
                                              }
                                            })}
                                        </TableBody>
                                      </Table>
                                    </AccordionDetails>
                                  </Accordion>
                                )}
                              </>
                            ) : item.description === ITEM_KEYS.EXPENSES ? (
                              expenseRows && expenseRows.length > 0 ? (
                                <Accordion
                                  sx={{
                                    boxShadow: "none",
                                    padding: "0px",
                                    margin: "0px",
                                    "&:before": { display: "none" },
                                    "&:hover": { backgroundColor: "white" },
                                    "& .MuiAccordionSummary-root": { flexDirection: "row-reverse" },
                                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": { transform: "rotate(90deg)" },
                                    "& .MuiAccordionSummary-content": { margin: "0", padding: "0", minHeight: "0" },
                                    "& .MuiAccordionSummary-content.Mui-expanded": { margin: "5px 0" },
                                    "& .MuiAccordionSummary-expandIconWrapper": { marginRight: "6px" },
                                  }}
                                  expanded={expandedAccordion === 'expenses'}
                                  onChange={() => handleAccordianClick('expenses')}
                                >
                                  <AccordionSummary
                                    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '10.5px', padding: "0", margin: "0" }} />}
                                    aria-controls="panel1a-content"
                                    id="panel1a-header"
                                    sx={{
                                      padding: "0px",
                                      margin: "0px",
                                      minHeight: "0px",
                                      "&.MuiAccordionSummary-root": { minHeight: "0px" },
                                    }}
                                  >
                                    <Typography sx={{ fontSize: "10.5px", fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif", }}>{getTranslatedDescription(ITEM_KEYS.EXPENSES)}</Typography>
                                  </AccordionSummary>
                                  <AccordionDetails sx={{ marginTop: 0, padding: "8px 16px 26px" }}>
                                    <Table
                                      sx={{
                                        width: "130%",
                                        padding: "0px",
                                        borderCollapse: "collapse",
                                        fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif", // 
                                      }}
                                    >
                                      <TableHead>
                                        <TableRow sx={{ height: "28px", minHeight: "28px", fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif", }}>
                                          <TableCell sx={{ fontSize: "0.8rem", padding: "4px 8px", fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif", }}>{t('common:date')}</TableCell>
                                          <TableCell sx={{ fontSize: "0.8rem", padding: "4px 8px", fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif", }}>{t('common:owner')}</TableCell>
                                          <TableCell sx={{ fontSize: "0.8rem", padding: "4px 8px", fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif", }}>{t('common:type')}</TableCell>
                                          <TableCell sx={{ fontSize: "0.8rem", padding: "4px 8px", fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif", }}>{t('forms:labels.description')}</TableCell>
                                          <TableCell sx={{ fontSize: "0.8rem", padding: "4px 8px", fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif", }}>{t('common:quantity')}</TableCell>
                                          <TableCell sx={{ fontSize: "0.8rem", padding: "4px 8px", fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif", }}>{t('common:rate_euro')}</TableCell>
                                          <TableCell sx={{ fontSize: "0.8rem", padding: "4px 8px", fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif", }}>{t('common:amount_euro')}</TableCell>
                                          <TableCell sx={{ fontSize: "0.8rem", padding: "4px 8px", fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif", }}></TableCell>
                                        </TableRow>
                                      </TableHead>

                                      <TableBody>
                                        {expenseRows.map((rowData, index) => (
                                          <TableRow key={index} sx={{ height: "36px", minHeight: "36px", padding: "8px 0" }}>
                                            <TableCell sx={{ padding: "6px", width: "15%" }}>
                                              <TextField
                                                type="date"
                                                name="date"
                                                value={rowData.date}
                                                onChange={(e) => handleExpenseChange(e, index)}
                                                disabled={!rowData.isEditable}
                                                sx={{
                                                  fontSize: "0.8rem",
                                                  fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
                                                  height: "30px",
                                                  "& .MuiInputBase-root": { height: "30px", fontSize: "0.8rem" }
                                                }}
                                              />
                                            </TableCell>

                                            <TableCell sx={{ padding: "6px", width: "18%" }}>
                                              <FormControl fullWidth>
                                                <InputLabel sx={{ fontSize: "0.8rem", display: "none" }}>Owner</InputLabel>
                                                <TextField
                                                  name="owner"
                                                  value={
                                                    filteredRelationships?.find((rel) => rel._id === formData?.from)
                                                      ? `${filteredRelationships?.find((rel) => rel._id === formData?.from)?.firstName} ${filteredRelationships?.find((rel) => rel._id === formData?.from)?.lastName}`
                                                      : ""
                                                  }
                                                  onChange={(e) => handleExpenseChange(e, index)}
                                                  disabled={true}
                                                  fullWidth
                                                  InputProps={{
                                                    startAdornment: formData?.from ? (
                                                      <InputAdornment position="start">
                                                        <Avatar
                                                          src={
                                                            filteredRelationships?.find((rel) => rel._id === formData?.from)?.avatar
                                                          }
                                                          sx={{
                                                            width: "1rem", // Adjust avatar size
                                                            height: "1rem",
                                                            marginRight: "1px",
                                                            fontSize: "7.8px"
                                                          }}
                                                        />
                                                      </InputAdornment>
                                                    ) : null,
                                                    sx: {
                                                      height: "30px",
                                                      fontSize: "0.8rem",
                                                      fontFamily:
                                                        "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
                                                    },
                                                  }}
                                                  sx={{
                                                    "& .MuiInputBase-root": {
                                                      height: "30px",
                                                      fontSize: "0.8rem",
                                                    },
                                                  }}
                                                />

                                              </FormControl>
                                            </TableCell>

                                            <TableCell sx={{ padding: "6px", width: "10%" }}>
                                              <FormControl fullWidth>
                                                <InputLabel sx={{ fontSize: "0.8rem", display: "none" }}>Type</InputLabel>
                                                <Select
                                                  name="type"
                                                  value={rowData.type}
                                                  onChange={(e) => handleExpenseChange(e, index)}
                                                  disabled={!rowData.isEditable}
                                                  displayEmpty
                                                  sx={{ fontSize: "0.8rem", height: "30px", fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif", }}
                                                >
                                                  <MenuItem disabled value="" sx={{ fontSize: "0.8rem" }}>{t('common:select_type')}</MenuItem>
                                                  <MenuItem value={EXPENSE_TYPES.KILOMETER} sx={{ fontSize: "0.8rem" }}>{t('common:kilometer')}</MenuItem>
                                                  <MenuItem value={EXPENSE_TYPES.COST} sx={{ fontSize: "0.8rem" }}>{t('common:cost')}</MenuItem>
                                                </Select>
                                              </FormControl>
                                            </TableCell>

                                            <TableCell sx={{ padding: "6px", width: "32%" }}>
                                              <TextField
                                                name="description"
                                                value={rowData.description}
                                                onChange={(e) => handleExpenseChange(e, index)}
                                                disabled={!rowData.isEditable}
                                                fullWidth
                                                placeholder={t('forms:placeholders.enter_description')}
                                                sx={{
                                                  fontSize: "0.8rem",
                                                  fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
                                                  height: "30px",
                                                  "& .MuiInputBase-root": { height: "30px", fontSize: "0.8rem" }
                                                }}
                                              />
                                            </TableCell>

                                            <TableCell sx={{ padding: "6px", width: "8%" }}>
                                              <TextField
                                                type="number"
                                                name="quantity"
                                                value={rowData.quantity}
                                                onChange={(e) => handleExpenseChange(e, index)}
                                                disabled={!rowData.isEditable || rowData.type !== EXPENSE_TYPES.KILOMETER}
                                                fullWidth
                                                placeholder={t('common:quantity')}
                                                sx={{
                                                  fontSize: "0.8rem",
                                                  fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
                                                  height: "30px",
                                                  "& .MuiInputBase-root": { height: "30px", fontSize: "0.8rem" }
                                                }}
                                              />
                                            </TableCell>

                                            <TableCell sx={{ padding: "6px", width: "8%" }}>
                                              <TextField
                                                type="number"
                                                name="rate"
                                                value={rowData.rate}
                                                onChange={(e) => handleExpenseChange(e, index)}
                                                disabled={!rowData.isEditable}
                                                fullWidth
                                                placeholder={rowData.type === EXPENSE_TYPES.KILOMETER ? t('common:rate') : t('common:amount')}
                                                sx={{
                                                  fontSize: "0.8rem",
                                                  fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
                                                  height: "30px",
                                                  "& .MuiInputBase-root": { height: "30px", fontSize: "0.8rem" }
                                                }}
                                              />
                                            </TableCell>

                                            <TableCell sx={{ padding: "6px", width: "18%" }}>
                                              <TextField
                                                type="number"
                                                name="amount"
                                                value={rowData.amount}
                                                disabled={true}
                                                fullWidth
                                                sx={{
                                                  fontSize: "0.8rem",
                                                  fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
                                                  height: "30px",
                                                  "& .MuiInputBase-root": { height: "30px", fontSize: "0.8rem" }
                                                }}
                                              />
                                            </TableCell>

                                            <TableCell sx={{ padding: "6px", display: "flex", alignItems: "center" }}>
                                              {rowData.isEditable ? (
                                                <IconButton onClick={() => handleSaveExpense(index)} sx={{ padding: "4px", color: 'rgb(0,91,146)' }}>
                                                  <SaveIcon fontSize="small" />
                                                </IconButton>
                                              ) : (
                                                <IconButton onClick={() => handleEditExpense(index)} sx={{ padding: "4px", color: 'rgb(0,91,146)' }}>
                                                  <EditIcon fontSize="small" />
                                                </IconButton>
                                              )}
                                              <IconButton onClick={() => handleRemoveExpenseRow(index)} sx={{ padding: "4px", color: 'rgb(0,91,146)' }}>
                                                <CloseIcon fontSize="small" />
                                              </IconButton>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>

                                  </AccordionDetails>
                                </Accordion>
                              ) : (
                                <Typography sx={{ fontSize: "10.5px", fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif", }}>{getTranslatedDescription(ITEM_KEYS.EXPENSES)}</Typography>
                              )
                            ) :
                              item.description === ITEM_KEYS.HIGH_DISBURSEMENT ? (
                                <>
                                  {files?.find(f => f._id === formData.case)?.rateConfig?.type === 'fixed' ? (
                                    <Typography sx={{ fontSize: "10.5px" }}>{getTranslatedDescription(ITEM_KEYS.HIGH_DISBURSEMENT)}</Typography>
                                  ) : (
                                    <Accordion
                                      sx={{
                                        boxShadow: "none",
                                        padding: "0px",
                                        margin: "0px",
                                        "&:before": { display: "none" },
                                        "&:hover": { backgroundColor: "white" },
                                        "& .MuiAccordionSummary-root": { flexDirection: "row-reverse" },
                                        "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": { transform: "rotate(90deg)" },
                                        "& .MuiAccordionSummary-content": { margin: "0", padding: "0", minHeight: "0" },
                                        "& .MuiAccordionSummary-content.Mui-expanded": { margin: "5px 0" },
                                        "& .MuiAccordionSummary-expandIconWrapper": { marginRight: "6px" },
                                      }}
                                      expanded={expandedAccordion === 'highDisbursement'}
                                      onChange={() => handleAccordianClick('highDisbursement')}
                                    >
                                      <AccordionSummary
                                        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '10.5px', padding: "0", margin: "0" }} />}
                                        aria-controls="panel1a-content"
                                        id="panel1a-header"
                                        sx={{
                                          padding: "0px",
                                          margin: "0px",
                                          minHeight: "0px",
                                          "&.MuiAccordionSummary-root": { minHeight: "0px" },
                                        }}
                                      >
                                        <Typography sx={{ fontSize: "10.5px", fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif" }}>
                                          {t('forms:billing.items.high_disbursement')}
                                        </Typography>
                                      </AccordionSummary>
                                      <AccordionDetails
                                        sx={{
                                          marginTop: 0,
                                          padding: "8px 16px 26px",
                                          fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
                                        }}
                                      >
                                        <Box sx={{ fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif" }}>
                                          <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            sx={{ fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif" }}
                                          >
                                            {disbursements?.filter(ds =>
                                              ds.case?._id === formData.case &&
                                              !ds.isBilled &&
                                              matchesBillingPeriod(ds)
                                            ).length === 0
                                              ? t('messages:info.no_disbursements_found', 'No disbursements found for this case.')
                                              : t('messages:info.add_more_disbursements', 'Add more disbursements if needed.')
                                            }
                                            {/* {filteredDisbursements.length === 0
                                              ? "No disbursements found for this case."
                                              : "Add more disbursements if needed."} */}
                                          </Typography>
                                          <Button
                                            variant="text"
                                            color="primary"
                                            onClick={() => handleSelectAllDisbursements()}
                                            sx={{
                                              marginTop: 0,
                                              fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
                                            }}
                                          >
                                            {t('buttons:confirmation.select_all', 'Select All')}
                                          </Button>
                                          <Button
                                            variant="text"
                                            color="primary"
                                            startIcon={<AddIcon />}
                                            onClick={() => navigate("/disbursements/create", { state: { returnTo: "/finance/create-invoice", caseId: formData.case } })}
                                            sx={{ marginTop: 0, fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif" }}
                                          >
                                            {t('buttons:confirmation.add_disbursement', 'Add Disbursement')}
                                          </Button>
                                        </Box>
                                        <Table sx={{ fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif" }}>
                                          <TableHead>
                                            <TableRow>
                                              <TableCell
                                                sx={{
                                                  fontWeight: "bold",
                                                  color: "#333",
                                                  padding: "8px",
                                                }}
                                              >
                                                {t('buttons:secondary.select')}
                                              </TableCell>
                                              <TableCell
                                                sx={{
                                                  fontWeight: "bold",
                                                  color: "#333",
                                                  padding: "8px",
                                                }}
                                              >
                                                {t('forms:labels.type')}
                                              </TableCell>
                                              <TableCell
                                                sx={{
                                                  fontWeight: "bold",
                                                  color: "#333",
                                                  padding: "8px",
                                                }}
                                              >
                                                {t('forms:billing.table_headers.date')}
                                              </TableCell>
                                              <TableCell
                                                sx={{
                                                  fontWeight: "bold",
                                                  color: "#333",
                                                  padding: "8px",
                                                }}
                                              >
                                                {t('forms:billing.table_headers.amount')} (€)
                                              </TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                          {disbursements
                                            ?.filter((ds) => {
                                              const isDisbursementSelected = selectedDisbursements.some(
                                                (selectedDs) => selectedDs._id === ds._id
                                              );

                                              // Always show disbursements for this case in the billing period.
                                              // We no longer hide them just because they are billed; linking/unlinking
                                              // is handled on save, not by hiding rows.
                                              return (
                                                ds.case &&
                                                ds.case._id === formData.case &&
                                                matchesBillingPeriod(ds)
                                              );
                                            })
                                            .map((disbursement, index) => (
                                              <TableRow
                                                key={index}
                                                sx={{
                                                  "&:hover": {
                                                    backgroundColor: "#f5f5f5",
                                                  },
                                                  height: "36px", // Match the height of timesheet rows
                                                }}
                                              >
                                                <TableCell
                                                  sx={{
                                                    padding: "8px",
                                                  }}
                                                >
                                                  <Checkbox
                                                    checked={selectedDisbursements.some(
                                                      (selectedDisb) => selectedDisb._id === disbursement._id
                                                    )}
                                                    onChange={() => handleDisbursementSelect(disbursement._id)}
                                                  />
                                                </TableCell>
                                                <TableCell
                                                  sx={{
                                                    padding: "8px",
                                                  }}
                                                >
                                                  {disbursement.type}
                                                </TableCell>
                                                <TableCell
                                                  sx={{
                                                    padding: "8px",
                                                  }}
                                                >
                                                  {new Date(disbursement.date).toLocaleDateString()}
                                                </TableCell>
                                                <TableCell
                                                  sx={{
                                                    padding: "8px",
                                                  }}
                                                >
                                                  € {disbursement.amount.toFixed(2)}
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </AccordionDetails>
                                    </Accordion>
                                  )}
                                </>
                              ) :

                                (
                                  getTranslatedDescription(item.description)
                                )}
                          </TableCell>
                          {item.description === ITEM_KEYS.VAT ? (
                            <>
                              <TableCell
                                sx={{
                                  padding: "0 8px",
                                  verticalAlign: "top",
                                  textAlign: "center",

                                }}
                              >
                                <TextField
                                  type="number"
                                  variant="standard"
                                  value={vatPercentage}
                                  onChange={(e) => setVatPercentage(Number(e.target.value))}
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    disableUnderline: false,
                                  }}
                                  sx={{
                                    width: "70px",
                                    // border: "none",
                                    "&:hover": {
                                      // borderBottom: "1px solid #333",
                                      boxSizing: "border-box",
                                      backgroundColor: "#f5f5f5",
                                    },
                                    "& input": { textAlign: "center" }
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                sx={{
                                  padding: "0 8px",
                                  verticalAlign: "top",
                                }}
                              >
                                <TextField
                                  type="number"
                                  variant="standard"
                                  name="value"
                                  value={item.value}
                                  sx={{
                                    paddingLeft: "8px",
                                  }}
                                  fullWidth
                                  InputProps={{
                                    disableUnderline: true,
                                    readOnly: true,
                                  }}
                                />
                              </TableCell>
                            </>
                          ) :
                            item.description === ITEM_KEYS.OFFICE_EXPENSES_ON_FEES ? (
                              <>
                                <TableCell
                                  sx={{
                                    padding: "0 8px",
                                    verticalAlign: "top",
                                    textAlign: "center",
                                  }}
                                >
                                  <TextField
                                    type="number"
                                    variant="standard"
                                    value={officeExpensesPercentage}
                                    onChange={(e) => setOfficeExpensesPercentage(Number(e.target.value))}
                                    InputProps={{
                                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                      disableUnderline: false,
                                    }}
                                    sx={{
                                      width: "70px",
                                      textAlign: "center",
                                      "&:hover": {
                                        boxSizing: "border-box",
                                        backgroundColor: "#f5f5f5",
                                      }
                                    }}
                                  />
                                </TableCell>
                                <TableCell
                                  sx={{
                                    padding: "0 8px",
                                    verticalAlign: "top",
                                  }}
                                >
                                  <TextField
                                    type="number"
                                    variant="standard"
                                    name="value"
                                    value={item.value}
                                    sx={{
                                      paddingLeft: "8px",
                                    }}
                                    fullWidth
                                    InputProps={{
                                      disableUnderline: true,
                                      readOnly: true,
                                    }}
                                  />
                                </TableCell>
                              </>
                            )
                              : (
                                <>
                                  <TableCell
                                    sx={{
                                      padding: "0 8px",
                                      verticalAlign: "top",
                                      textAlign: "center",
                                    }}
                                  >
                                    {/* Empty cell for non-VAT rows */}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      padding: "0 8px",
                                      verticalAlign: "top",
                                      ...(!Object.values(ITEM_KEYS).filter(key => [ITEM_KEYS.FEES, ITEM_KEYS.EXPENSES, ITEM_KEYS.HIGH_DISBURSEMENT].includes(key)).includes(item.description) && isInputActive && {
                                        "&:hover": {
                                          borderBottom: "1px solid #333",
                                          boxSizing: "border-box",
                                          backgroundColor: "#f5f5f5",
                                        },
                                      }),
                                    }}
                                  >
                                    {[ITEM_KEYS.FEES, ITEM_KEYS.EXPENSES, ITEM_KEYS.HIGH_DISBURSEMENT].includes(item.description) ? (
                                      <TextField
                                        type="number"
                                        variant="standard"
                                        name="value"
                                        value={item.value}
                                        sx={{
                                          paddingLeft: "8px",
                                        }}
                                        fullWidth
                                        InputProps={{
                                          disableUnderline: true,
                                          readOnly: true,
                                        }}
                                      />
                                    ) : (
                                      <TextField
                                        type="number"
                                        variant="standard"
                                        name="value"
                                        value={item.value}
                                        onFocus={() => setIsInputActive(true)}
                                        onBlur={() => setIsInputActive(false)}
                                        onChange={(e) => {
                                          const updatedItems = [...formData.items];
                                          updatedItems[index].value = parseFloat(
                                            e.target.value
                                          );
                                          setFormData({
                                            ...formData,
                                            items: updatedItems,
                                          });
                                        }}
                                        sx={{
                                          paddingLeft: "8px",
                                        }}
                                        fullWidth
                                        InputProps={{
                                          disableUnderline: true,
                                        }}
                                      />
                                    )}
                                  </TableCell>
                                </>
                              )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </Grid>
          </Grid>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-end",
                backgroundColor: "#f9f9f9",
                // marginRight: "5%",
              }}
            >
              <Card
                sx={{
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: "5px",
                  boxShadow: "none",
                  border: "none",
                }}
                elevation={0}
              >
                <CardContent sx={{ padding: "8px" }}>
                  <Typography
                    variant="subtitle1"
                    component="div"
                    sx={{ fontWeight: "bold" }}
                  >
                    {t('pages:invoices.due_total')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "1.2rem" }}
                  >
                    € {Number(formData.total).toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
              <Card
                sx={{
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: "5px",
                  boxShadow: "none",
                  border: "none",
                }}
                elevation={0}
              >
                <CardContent sx={{ padding: "8px" }}>
                  <Typography
                    variant="subtitle1"
                    component="div"
                    sx={{ fontWeight: "bold" }}
                  >
                    {t('forms:billing.items.vat')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "1.2rem" }}
                  >
                    € {Number(formData.VAT).toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              // padding: "10px 16px",
              // background: "#fff",
              zIndex: 10,
              backgroundColor: "white",
              color: "#000000",
              padding: "13px 80px",
            }}
          >
            <CancelButton
              variant="outlined"
              onClick={handleCancel}
              size="small"
            >
              {t('buttons:secondary.cancel')}
            </CancelButton>
            <SubmitButton onClick={handleSubmit} size="small">
              {loading ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : (
                isEditing ? t('pages:finance.update_invoice') : t('pages:finance.create_invoice')
              )}
            </SubmitButton>
          </Box>
        </form>
      </Box>
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreateInvoicePage;
